import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "bomia-theme";

interface ThemeStorage {
  setItem: (key: string, value: string) => void;
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => themeFromDocument());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => current === "dark" ? "light" : "dark");
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme deve ser usado dentro de ThemeProvider.");
  }
  return value;
}

export function resolveStoredTheme(value: string | null): Theme {
  return value === "light" ? "light" : "dark";
}

export function persistTheme(theme: Theme, storage: ThemeStorage): void {
  storage.setItem(THEME_STORAGE_KEY, theme);
}

function themeFromDocument(): Theme {
  if (typeof document === "undefined") {
    return "dark";
  }
  return resolveStoredTheme(document.documentElement.dataset.theme ?? null);
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.querySelector<HTMLMetaElement>("meta[name='theme-color']")
    ?.setAttribute("content", theme === "dark" ? "#0b0f17" : "#f4f6f9");
  try {
    persistTheme(theme, localStorage);
  } catch {
    // A interface continua funcional quando o armazenamento local está indisponível.
  }
}
